/**
 * Operator Program - Explicit Periodization Configuration
 * 13-week Block Periodization Model
 */

import type { ProgramPeriodizationModel } from './types/program';

export const OPERATOR_PERIODIZATION: ProgramPeriodizationModel = {
  style: 'block',
  phases: [
    // Week 1: Baseline Assessment
    {
      weeks: [1],
      type: 'realization',
      name: 'Baseline Testing',
      focus: 'Establish current fitness levels and set training maxes',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    },
    
    // Week 2-6: Accumulation Phase (TRUE BLOCK PERIODIZATION - Volume Focus)
    {
      weeks: [2, 3, 4, 5, 6],
      type: 'accumulation',
      name: 'Accumulation Block',
      focus: 'Building work capacity, muscle mass, and movement quality through high volume',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.65,     // Week 2: 65% (moderate load)
        endIntensity: 0.70,       // Week 6: 70%
        weeklyIncrease: 0.0125    // +1.25% per week (65% → 66.25% → 67.5% → 68.75% → 70%)
      },
      trainingParameters: {
        repRange: [8, 12],        // Hypertrophy rep range - BASE template reps will be multiplied
        setsMultiplier: 1.0,      // Maintain base sets from template
        restPeriod: '60-90s'      // Shorter rest for metabolic stress
      }
    },
    
    // Week 7: Deload (Transition Between Blocks)
    {
      weeks: [7],
      type: 'deload',
      name: 'Active Recovery Week',
      focus: 'Recovery and adaptation - prepare for intensification phase',
      intensityMultiplier: 0.7,     // 70% of normal intensity
      volumeMultiplier: 0.6,        // 60% of normal volume
      trainingParameters: {
        repRange: [5, 8],
        setsMultiplier: 0.6,
        restPeriod: '2-3min'
      }
    },
    
    // Week 8-12: Intensification Phase (TRUE BLOCK PERIODIZATION - Strength Focus)
    {
      weeks: [8, 9, 10, 11, 12],
      type: 'intensification',
      name: 'Intensification Block',
      focus: 'Converting accumulated capacity into maximum strength and power',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.85,     // Week 8: 85% (heavy load)
        endIntensity: 0.91,       // Week 12: 91%
        weeklyIncrease: 0.015     // +1.5% per week (85% → 86.5% → 88% → 89.5% → 91%)
      },
      trainingParameters: {
        repRange: [2, 4],         // Low rep strength range - BASE template reps will be divided
        setsMultiplier: 1.25,     // 25% more sets for intensification (4 → 5 sets)
        restPeriod: '3-5min'      // Full CNS recovery between sets
      }
    },
    
    // Week 13: Final Assessment
    {
      weeks: [13],
      type: 'realization',
      name: 'Final Assessment',
      focus: 'Demonstrate peak performance and new capabilities',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    }
  ]
};

/**
 * Generate periodization model for custom programs
 * Based on program duration and goals
 */
export function generateAutoPeriodization(
  duration: number,
  experienceLevel?: 'beginner' | 'intermediate' | 'advanced' | 'elite'
): ProgramPeriodizationModel {
  // 4-week undulating program (for beginners or short cycles)
  if (duration === 4) {
    return {
      style: 'undulating',
      phases: [
        { weeks: [1], type: 'accumulation', name: 'Volume Week 1', focus: 'Building work capacity', intensityMultiplier: 1.0, volumeMultiplier: 1.0 },
        { weeks: [2], type: 'intensification', name: 'Intensity Week', focus: 'Higher load, lower volume', intensityMultiplier: 1.1, volumeMultiplier: 0.8 },
        { weeks: [3], type: 'accumulation', name: 'Volume Week 2', focus: 'Continued capacity building', intensityMultiplier: 1.0, volumeMultiplier: 1.0 },
        { weeks: [4], type: 'deload', name: 'Recovery Week', focus: 'Active recovery and adaptation', intensityMultiplier: 0.7, volumeMultiplier: 0.6 }
      ]
    };
  }
  
  // 5-7 week linear program
  if (duration >= 5 && duration <= 7) {
    return {
      style: 'linear',
      phases: [
        { weeks: Array.from({ length: duration - 1 }, (_, i) => i + 1), type: 'accumulation', name: 'Build Phase', focus: 'Progressive volume and intensity', intensityMultiplier: 1.0, volumeMultiplier: 1.0 },
        { weeks: [duration], type: 'deload', name: 'Recovery Week', focus: 'Active recovery before next cycle', intensityMultiplier: 0.7, volumeMultiplier: 0.6 }
      ]
    };
  }
  
  // 8-12 week block periodization (most common)
  if (duration >= 8 && duration <= 12) {
    const midPoint = Math.ceil(duration / 2);
    const phases: ProgramPeriodizationModel['phases'] = [];
    
    // Optional: Assessment week 1 (for intermediate+)
    if (experienceLevel !== 'beginner') {
      phases.push({
        weeks: [1],
        type: 'realization',
        name: 'Baseline Assessment',
        focus: 'Establish current fitness levels',
        intensityMultiplier: 1.0,
        volumeMultiplier: 1.0
      });
    }
    
    // Accumulation phase (first half or first half - 1)
    const accumulationStart = experienceLevel !== 'beginner' ? 2 : 1;
    const accumulationEnd = midPoint;
    phases.push({
      weeks: Array.from({ length: accumulationEnd - accumulationStart + 1 }, (_, i) => i + accumulationStart),
      type: 'accumulation',
      name: 'Volume Phase',
      focus: 'Building work capacity and volume tolerance',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    });
    
    // Optional: Mid-cycle deload
    if (duration >= 10) {
      phases.push({
        weeks: [midPoint + 1],
        type: 'deload',
        name: 'Mid-Cycle Recovery',
        focus: 'Reset and prepare for intensification',
        intensityMultiplier: 0.7,
        volumeMultiplier: 0.6
      });
    }
    
    // Intensification phase (second half)
    const intensificationStart = duration >= 10 ? midPoint + 2 : midPoint + 1;
    const intensificationEnd = duration - 1;
    if (intensificationStart <= intensificationEnd) {
      phases.push({
        weeks: Array.from({ length: intensificationEnd - intensificationStart + 1 }, (_, i) => i + intensificationStart),
        type: 'intensification',
        name: 'Strength Phase',
        focus: 'Building peak strength with reduced volume',
        intensityMultiplier: 1.1,
        volumeMultiplier: 0.8
      });
    }
    
    // Final week: deload or realization
    phases.push({
      weeks: [duration],
      type: experienceLevel !== 'beginner' ? 'realization' : 'deload',
      name: experienceLevel !== 'beginner' ? 'Final Assessment' : 'Recovery Week',
      focus: experienceLevel !== 'beginner' ? 'Test peak performance' : 'Active recovery',
      intensityMultiplier: experienceLevel !== 'beginner' ? 1.0 : 0.7,
      volumeMultiplier: experienceLevel !== 'beginner' ? 1.0 : 0.6
    });
    
    return {
      style: 'block',
      phases
    };
  }
  
  // 13+ week advanced block periodization
  if (duration >= 13) {
    return {
      style: 'block',
      phases: [
        { weeks: [1], type: 'realization', name: 'Baseline Assessment', focus: 'Establish current fitness levels', intensityMultiplier: 1.0, volumeMultiplier: 1.0 },
        { weeks: Array.from({ length: 4 }, (_, i) => i + 2), type: 'accumulation', name: 'Accumulation Phase 1', focus: 'Volume build - first block', intensityMultiplier: 1.0, volumeMultiplier: 1.0 },
        { weeks: [6], type: 'deload', name: 'Deload Week 1', focus: 'Recovery and adaptation', intensityMultiplier: 0.7, volumeMultiplier: 0.6 },
        { weeks: [7], type: 'realization', name: 'Mid-Cycle Check', focus: 'Reassess progress', intensityMultiplier: 1.0, volumeMultiplier: 1.0 },
        { weeks: Array.from({ length: 4 }, (_, i) => i + 8), type: 'intensification', name: 'Intensification Phase', focus: 'Strength build with progressive intensity', intensityMultiplier: 1.1, volumeMultiplier: 0.8 },
        { weeks: [12], type: 'deload', name: 'Deload Week 2', focus: 'Pre-peak recovery', intensityMultiplier: 0.7, volumeMultiplier: 0.6 },
        { weeks: [duration], type: 'realization', name: 'Final Assessment', focus: 'Demonstrate peak performance', intensityMultiplier: 1.0, volumeMultiplier: 1.0 }
      ]
    };
  }
  
  // Fallback: simple linear
  return {
    style: 'linear',
    phases: [
      { weeks: Array.from({ length: duration }, (_, i) => i + 1), type: 'accumulation', name: 'Training Phase', focus: 'Progressive training', intensityMultiplier: 1.0, volumeMultiplier: 1.0 }
    ]
  };
}


